import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import AuthContext from './AuthContext';
import API_BASE_URL from '../config/api';
import { devLog } from '../utils/devLog';

/**
 * PluginContext
 *
 * Tracks which opt-in business-feature plugins the current designer
 * has installed. Used by the sidebar (conditional nav items) and
 * PluginGuard (route-level gating).
 *
 * Source of truth is `GET /api/plugins/enabled/` which returns:
 *   { "enabled": ["unified_orders", "production", ...] }
 */

const PluginContext = createContext({
  enabledPlugins: new Set(),
  isPluginEnabled: () => false,
  loading: true,
  refresh: () => Promise.resolve(),
});

export const PluginProvider = ({ children }) => {
  const { authToken } = useContext(AuthContext);
  const [enabledPlugins, setEnabledPlugins] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const fetchEnabled = useCallback(async () => {
    if (!authToken?.access) {
      setEnabledPlugins(new Set());
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/plugins/enabled/`,
        {
          headers: { Authorization: `Bearer ${authToken.access}` },
        }
      );
      const list = Array.isArray(response.data?.enabled)
        ? response.data.enabled
        : [];
      setEnabledPlugins(new Set(list));
    } catch (err) {
      devLog.error('Failed to fetch enabled plugins:', err);
      // Soft-fail: leave set empty so gated pages show install CTA
      // instead of blocking the whole app.
      setEnabledPlugins(new Set());
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    setLoading(true);
    fetchEnabled();
  }, [fetchEnabled]);

  const isPluginEnabled = useCallback(
    (key) => enabledPlugins.has(key),
    [enabledPlugins]
  );

  const value = useMemo(
    () => ({
      enabledPlugins,
      isPluginEnabled,
      loading,
      refresh: fetchEnabled,
    }),
    [enabledPlugins, isPluginEnabled, loading, fetchEnabled]
  );

  return (
    <PluginContext.Provider value={value}>{children}</PluginContext.Provider>
  );
};

export const usePluginContext = () => useContext(PluginContext);

export default PluginContext;
